// A soft bell, synthesized with Web Audio so Zen needs no sound file: three
// inharmonic partials on a fast attack and a long exponential decay, which is
// what makes a struck bowl read as a bowl rather than a beep.
//
// Browsers only let audio start after a user gesture. `unlockChime()` is
// called from the clicks that arm a timer or an alarm, so the context is
// already running by the time the chime has to play on its own. When it isn't
// (an alarm restored after a reload, nobody has tapped yet), `playChime()`
// still tries; the ringing overlay asks for the tap that lets it sound.

const PARTIALS = [
    { ratio: 1, gain: 0.32, decay: 4.2 },
    { ratio: 2.76, gain: 0.12, decay: 2.4 },
    { ratio: 5.4, gain: 0.05, decay: 1.2 }
]

let context = null

function audioContext() {
    if (context) return context
    const AC = window.AudioContext || window.webkitAudioContext
    if (!AC) return null
    context = new AC()
    return context
}

export function unlockChime() {
    const ctx = audioContext()
    if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {})
}

export function chimeAudible() {
    return !!context && context.state === "running"
}

// One strike. `pitch` is the fundamental in Hz; `volume` scales every partial.
export function playChime({ pitch = 528, volume = 1 } = {}) {
    const ctx = audioContext()
    if (!ctx) return
    if (ctx.state === "suspended") ctx.resume().catch(() => {})

    const now = ctx.currentTime
    const master = ctx.createGain()
    master.gain.value = volume
    master.connect(ctx.destination)

    PARTIALS.forEach(({ ratio, gain, decay }) => {
        const osc = ctx.createOscillator()
        const env = ctx.createGain()
        osc.type = "sine"
        osc.frequency.value = pitch * ratio
        env.gain.setValueAtTime(0.0001, now)
        env.gain.exponentialRampToValueAtTime(gain, now + 0.012)
        env.gain.exponentialRampToValueAtTime(0.0001, now + decay)
        osc.connect(env)
        env.connect(master)
        osc.start(now)
        osc.stop(now + decay + 0.05)
    })
}

// The "chime when a timer ends" switch in Zen's Timer menu. On unless the
// viewer turned it off; a per-browser preference, so localStorage is enough.
const CHIME_OFF_KEY = "zen-timer-chime-off"

export function timerChimeEnabled() {
    try {
        return localStorage.getItem(CHIME_OFF_KEY) !== "1"
    } catch (_error) {
        return true
    }
}

export function setTimerChime(enabled) {
    try {
        if (enabled) {
            localStorage.removeItem(CHIME_OFF_KEY)
        } else {
            localStorage.setItem(CHIME_OFF_KEY, "1")
        }
    } catch (_error) {
        // Storage blocked: the switch just doesn't stick across reloads.
    }
}
